/**
 * CityHeatmap - City-level heat visualization overlay
 * Pro-only with blur overlay for free users
 */
import { useState, useEffect } from "react";
import { 
  Flame, MapPin, Lock, Sparkles, Globe, Building 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const CityHeatmap = ({ onCitySelect }) => {
  const { hasUniversalAccess, hasPaidAccess } = useEntitlements();
  const hasAccess = hasUniversalAccess || hasPaidAccess;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  
  useEffect(() => {
    fetchHeatmapData();
  }, []);
  
  const fetchHeatmapData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/premium/heatmaps`);
      setData(response.data);
      
      if (!response.data.is_premium && !hasAccess) {
        // Don't auto-show paywall, show blurred content instead
      }
    } catch (error) {
      console.error("Failed to fetch heatmap data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const getIntensityColor = (intensity) => {
    const colors = {
      1: "bg-coral/20 border-coral/30",
      2: "bg-coral/40 border-coral/50",
      3: "bg-coral/60 border-coral/70",
      4: "bg-coral/80 border-coral/90",
      5: "bg-coral border-coral",
    };
    return colors[intensity] || colors[1];
  };
  
  if (loading) {
    return (
      <Card className="bg-charcoal/95 border-coral/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Flame className="w-5 h-5 text-coral" />
              City Heatmap
            </CardTitle>
            {!hasAccess && (
              <Badge variant="outline" className="border-coral/50 text-coral text-xs">
                <Lock className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Hub Cities */}
          {hasAccess && data?.hub_cities?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white/80 mb-2 flex items-center gap-2">
                <Building className="w-4 h-4" />
                Your Travel Hubs
              </h4>
              <div className="space-y-2">
                {data.hub_cities.slice(0, 5).map((city, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => onCitySelect?.(city)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getIntensityColor(city.intensity)}`} />
                      <div>
                        <p className="text-white text-sm font-medium">{city.location}</p>
                        <p className="text-white/50 text-xs">{city.country_code}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-coral font-bold">{city.visits}</p>
                      <p className="text-white/50 text-xs">visits</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Continent Density */}
          {hasAccess && data?.continent_density && Object.keys(data.continent_density).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white/80 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Continent Density
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(data.continent_density)
                  .filter(([continent]) => continent !== "Unknown")
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 4)
                  .map(([continent, count]) => (
                    <div 
                      key={continent}
                      className="bg-white/5 rounded-lg p-3 text-center"
                    >
                      <p className="text-lg font-bold text-coral">{count}</p>
                      <p className="text-xs text-white/60">{continent}</p>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
          
          {/* Heat Legend */}
          {hasAccess && (
            <div className="pt-2 border-t border-white/10">
              <p className="text-xs text-white/50 mb-2">Heat Intensity</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <div 
                    key={i}
                    className={`flex-1 h-2 rounded ${getIntensityColor(i)}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
          )}
          
          {/* Stats */}
          {hasAccess && data?.total_unique_locations && (
            <div className="text-center pt-2">
              <p className="text-white/60 text-sm">
                <span className="text-coral font-bold">{data.total_unique_locations}</span> unique locations tracked
              </p>
            </div>
          )}
          
          {/* Blurred teaser for non-premium */}
          {!hasAccess && (
            <div className="relative">
              {/* Blurred content preview */}
              <div className="blur-md select-none pointer-events-none">
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-coral/50" />
                        <div>
                          <div className="h-4 w-24 bg-white/20 rounded" />
                          <div className="h-3 w-12 bg-white/10 rounded mt-1" />
                        </div>
                      </div>
                      <div className="h-6 w-8 bg-coral/30 rounded" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Overlay CTA */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-charcoal/95 rounded-lg p-6 text-center shadow-xl border border-coral/20">
                  <Flame className="w-10 h-10 text-coral mx-auto mb-3" />
                  <h4 className="text-white font-medium mb-1">City Heatmaps</h4>
                  <p className="text-xs text-white/60 mb-4">
                    {data?.teaser?.total_locations || 0} locations tracked
                  </p>
                  <Button
                    onClick={() => setShowPaywall(true)}
                    className="bg-coral hover:bg-coral/90"
                    size="sm"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Learn more on duolyst.co.uk
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Empty state */}
          {hasAccess && (!data?.heatmap_data || data.heatmap_data.length === 0) && (
            <div className="text-center py-6">
              <MapPin className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 text-sm">{data?.message || "Add trips with locations to generate heatmaps!"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showPaywall && (
        <PaywallScreen
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          type={PaywallType.CITY_HEATMAP}
          feature="city_heatmaps"
          title="City Heatmaps"
          description="Visualize your most visited cities and travel patterns."
        />
      )}
    </>
  );
};

export default CityHeatmap;
